import { Button, IconButton } from "@ai-teach/ui-web";
import { useMutation, useQuery } from "@apollo/client/react";
import { useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { SystemConfigDocument, ToggleSystemFsrsDocument } from "../../../generated/graphql";
import { ConfirmationModal } from "../../modal/ConfirmationModal";

export function SystemFSRSToggle() {
  const { user } = useAuth();
  const isSuperuser = user?.role === "super";
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingValue, setPendingValue] = useState(false);

  const { data, loading: isLoading, error, refetch } = useQuery(SystemConfigDocument);
  const [toggleFSRS, { loading: toggleLoading }] = useMutation(ToggleSystemFsrsDocument);

  const handleToggle = (enabled: boolean) => {
    setPendingValue(enabled);
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    try {
      await toggleFSRS({
        variables: { enabled: pendingValue },
      });
      await refetch();
      setShowConfirmation(false);
    } catch (err) {
      console.error("Failed to toggle FSRS:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-gray-700 dark:text-gray-500">Loading system settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-red-500">Error loading settings: {error.message}</div>
      </div>
    );
  }

  if (!data?.systemConfig) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-gray-700 dark:text-gray-500">No system configuration found</div>
      </div>
    );
  }

  const isEnabled = data.systemConfig.fsrsEnabled;

  return (
    <>
      <div className="panel p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="h5">FSRS System Status</h3>
            <p className="mt-1 caption">
              {isEnabled
                ? "FSRS is currently enabled for all schools"
                : "FSRS is currently disabled system-wide"}
            </p>
            {!isSuperuser && (
              <p className="mt-2 text-sm text-warning-500 font-medium">
                ⚠️ Super user access required to change this setting
              </p>
            )}
          </div>
          <Button
            type="button"
            onClick={() => handleToggle(!isEnabled)}
            disabled={toggleLoading || !isSuperuser}
            variant="ghost"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 p-0 ${
              isEnabled ? "bg-primary-600" : "bg-gray-100 dark:bg-gray-800"
            } ${toggleLoading || !isSuperuser ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </Button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirm}
        title={pendingValue ? "Enable FSRS System-Wide" : "Disable FSRS System-Wide"}
        message={
          pendingValue
            ? "This will enable FSRS spaced repetition for all schools. Are you sure?"
            : "This will disable FSRS for all schools. Existing card data will be preserved. Are you sure?"
        }
        confirmText={pendingValue ? "Enable" : "Disable"}
        variant={pendingValue ? "info" : "warning"}
        isLoading={toggleLoading}
      />
    </>
  );
}
